import { timestampDate } from '@bufbuild/protobuf/wkt'
import { BanIcon, DownloadIcon, EllipsisVertical, RefreshCcwIcon, Trash2Icon } from 'lucide-react'

import { useDeleteOrgArtifact, useDownloadArtifact } from '@/api/artifact.hook'
import { useRetryArtifact, useSetArtifactMisclassification } from '@/api/document.hook'
import { Artifact, DocumentSource } from '@/gen/artifacts/v1/model_pb'

import { DocumentStatus, toDocumentStatus } from '@/const/artifact-status.types'

import { useHasPermissions } from '@/lib/app/check-permissions.hook'
import { useIsSupportUser } from '@/lib/app/support-user'
import { isDownloadableArtifact } from '@/lib/file/artifact-download'

import { hasPublicDataService, isDevelopment, isPreview, isStaging } from '@/utils/env-utils.ts'

import { Button } from '@/components/lemmala-design-system/button/button'
import {
  DropdownMenu,
  type DropdownMenuActionItem,
} from '@/components/lemmala-design-system/dropdown/dropdown-menu'
import {
  ConfirmResult,
  useConfirmModal,
} from '@/components/lemmala-design-system/modal/use-confirm-modal'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'

type ArtifactActionDropdownProps = {
  artifact: Artifact
}

export const ArtifactActionDropdown = ({ artifact }: ArtifactActionDropdownProps) => {
  const { confirm } = useConfirmModal()
  const { mutateAsync: deleteOrgArtifact } = useDeleteOrgArtifact()
  const { downloadArtifact, isDownloadPending } = useDownloadArtifact()
  const { mutateAsync: retryArtifact } = useRetryArtifact()
  const isSupportUser = useIsSupportUser()
  const hasBaseManagePermissions = useHasPermissions(['base.manage'])

  const shouldShowArtifactsOptions = isDevelopment || isPreview || isStaging || isSupportUser

  const artifactIsMisclassifiable = artifact.source === DocumentSource.WEB && hasPublicDataService

  const status = toDocumentStatus(artifact.status)

  const documentIsRetryable =
    !!artifact.error ||
    (status !== DocumentStatus.COMPLETE &&
      artifact.dateUpdated &&
      new Date().getTime() - Number(timestampDate(artifact.dateUpdated)) > 60 * 60 * 1000)
  const isManualUpload = [
    DocumentSource.MANUAL_UPLOAD,
    DocumentSource.MANUAL_UPLOAD_WITH_COMPANY,
    DocumentSource.MANUAL_UPLOAD_WITH_COMPANY_AND_CATEGORY,
    DocumentSource.LEMA_QUESTIONNAIRE,
  ].includes(artifact.source)
  const isFinalState = DocumentStatus.COMPLETE === status || artifact.error
  const isArtifactIdCase = artifact.artifact.case === 'artifactId'

  const isDeleteable = isManualUpload && isFinalState && isArtifactIdCase
  const isDownloadable = isDownloadableArtifact(artifact)

  const showDeleteConfirm = async () => {
    const result = await confirm({
      variant: 'destructive',
      title: 'Delete artifact',
      primaryButtonText: 'Delete',
      content: `Are you sure you want to delete "${artifact.name}"?`,
    })
    if (result === ConfirmResult.CONFIRMED) await deleteOrgArtifact({ artifactId: artifact.id })
  }

  const { mutateAsync: setArtifactMisclassification } = useSetArtifactMisclassification()

  const getDeleteTooltipMessage = () => {
    switch (artifact.source) {
      case DocumentSource.WEB:
        return 'Deleting web artifacts is not supported'
      case DocumentSource.INTEGRATION:
        return 'Deleting artifacts from integrations is not supported'
    }

    switch (status) {
      case DocumentStatus.CLASSIFYING:
        return 'Cannot delete artifacts while in analysis'
    }

    return 'Delete not supported for this artifact'
  }

  // Download is the one item a base.view user gets, so the menu is gated per
  // item rather than as a whole -- gating the whole dropdown would take the 3-dot
  // button away from view users entirely, which is what it used to do.
  const menuItems: DropdownMenuActionItem[] = [
    {
      content: <TextWithIcon text='Download' icon={<DownloadIcon className='size-3.75' />} />,
      // Hidden, not disabled, when there is no file: a link-only or public
      // artifact has nothing to download and never will, so a greyed row would
      // just raise a question it can't answer.
      hide: !isDownloadable,
      itemDisabled: isDownloadPending,
      onClick: () => downloadArtifact(artifact),
    },
    {
      content: (
        <TextWithIcon
          className='text-main-foreground-danger'
          text='Delete'
          icon={<Trash2Icon className='size-3.75 text-main-foreground-danger' />}
        />
      ),
      hide: !hasBaseManagePermissions,
      disabledTooltip: getDeleteTooltipMessage(),
      itemDisabled: !isDeleteable,
      onClick: showDeleteConfirm,
    },
    {
      content: <TextWithIcon text='Retry' icon={<RefreshCcwIcon className='size-3.75' />} />,
      hide: !shouldShowArtifactsOptions || !hasBaseManagePermissions,
      itemDisabled: !documentIsRetryable,
      onClick: () => retryArtifact({ artifactId: artifact.id }),
    },
    {
      content: <TextWithIcon text='Set misclassified' icon={<BanIcon className='size-3.75' />} />,
      hide: !shouldShowArtifactsOptions || !hasBaseManagePermissions,
      itemDisabled: !artifactIsMisclassifiable,
      onClick: () =>
        setArtifactMisclassification({ artifactId: artifact.id, isMisclassified: true }),
    },
  ]

  // Without this the trigger would still render on rows where every item is
  // hidden, opening an empty menu.
  if (menuItems.every((item) => item.hide)) {
    return null
  }

  return (
    <DropdownMenu
      variant='secondary'
      triggerAppearance='headless'
      triggerContent={
        <Button
          variant={'ghost'}
          size={'icon-sm'}
          data-interactive='true'
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          <EllipsisVertical className='size-4' />
        </Button>
      }
      menuItems={menuItems}
    />
  )
}
